import type { InfiniteData } from '@tanstack/react-query';
import type { ScreensReadModel } from 'shared/src/api';
import { isNotNil } from 'shared/src/utils/isNotNil';

/**
 * useInfiniteQuery select 전용 파생 유틸 (VIEW 이벤트 지연 발화)
 *
 * iOS MetaFeedReactor 대응:
 * - should_defer_view_event=true + pagesCount≥2 → 1차+2차 logging 머지
 * - should_defer_view_event=true + pagesCount<2 + nextToken 존재 → 자동 prefetch 신호
 * - 그 외 → 기존 동작(lastPage.logging 그대로)
 *
 * 반환: lastPage 스프레드 + 누적 components + 파생 필드 4개
 */
export const applyDeferredViewEventSelect = (
  data: InfiniteData<ScreensReadModel>,
) => {
  const components = data.pages.flatMap(page => page.components);
  const firstPage = data.pages[0];
  const lastPage = data.pages[data.pages.length - 1];
  const pagesCount = data.pages.length;
  const shouldDeferViewEvent = firstPage.shouldDeferViewEvent ?? false;

  const isMerged = shouldDeferViewEvent && pagesCount >= 2;
  const loggingForViewEvent = isMerged
    ? {
        analytics: {
          ...firstPage.logging?.analytics,
          ...lastPage.logging?.analytics,
        },
        ably_only_log: {
          ...firstPage.logging?.ably_only_log,
          ...lastPage.logging?.ably_only_log,
        },
      }
    : lastPage.logging;

  const shouldAutoPrefetchNextPage =
    shouldDeferViewEvent && pagesCount < 2 && isNotNil(lastPage.nextToken);

  return {
    ...lastPage,
    components,
    shouldDeferViewEvent,
    pagesCount,
    loggingForViewEvent,
    shouldAutoPrefetchNextPage,
  };
};
